import { apiSlice } from './apiSlice'

/**
 * Report › Inputs › Cash Flow — Access `frmReportCashFlowMain` hub.
 * Each "Go to Page" entry reads a `v_report_cashflow_*` view; the dialogs write
 * the mapping / input-value base tables.
 */

export interface CashflowListResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface WeekColumn {
  weekCode: string
  weekName: string
  woy: string
  startDate: string | null
  endDate: string | null
}

export interface InputDetail {
  qualityCode: string | null
  inputType: string | null
  orderBy: string | null
  category: string | null
  description: string | null
  priceFromG2: number | null
  week00: number | null
  week01: number | null
  week02: number | null
  week03: number | null
  week04: number | null
  week05: number | null
  week06: number | null
  week07: number | null
  inputName: string | null
}

export interface BankBalance {
  bankAccountNumber: string | null
  bankName: string | null
  openAmountOc: number | null
  openAmount: number | null
  debitAmountOc: number | null
  debitAmount: number | null
  creditAmountOc: number | null
  creditAmount: number | null
  closeAmountOc: number | null
  closeAmount: number | null
  bankFx: number | null
  bankBranchName: string | null
  isSudung: string | null
  description: string | null
  address: string | null
  reloadDatetime: string | null
}

export interface MapCp {
  checkType: string | null
  isMap: string | null
  erpCode: string | null
  erpShortName: string | null
  erpName: string | null
  erpVat: string | null
  misaCode: string | null
  misaName: string | null
  misaVat: string | null
  isVender: string | null
  isCustomer: string | null
  isEmployee: string | null
  hadPcontract: string | null
  hadScontract: string | null
  misaLstInvoice: string | null
  misaNgoaile: string | null
  misaId: string | null
}

export interface MapService {
  isMapped: string | null
  serviceCode: string | null
  serviceTypeName: string | null
  serviceName: string | null
  misaCode: string | null
  misaName: string | null
  misaAddress: string | null
  misaVatCode: string | null
  tlcPic: string | null
  tel: string | null
  misaId: string | null
  address: string | null
  taxNo: string | null
  note: string | null
  serviceType: string | null
}

export interface MisaObject {
  misaCode: string | null
  misaName: string | null
  misaAddress: string | null
  misaVatCode: string | null
  erpCode: string | null
  erpName: string | null
  mappedTxt: string | null
  misaId: string | null
}

export interface MapMuaHang {
  mappedTxt: string | null
  sohoadonCheckdate: string | null
  erpCp: string | null
  erpMisaCode: string | null
  erpTabName: string | null
  erpInvNo: string | null
  invoiceDate: string | null
  invoiceAmount: number | null
  erpInvNote: string | null
  tabName: string | null
  nhacungcap: string | null
  sochungtu: string | null
  sohoadon: string | null
  ngayhoadon: string | null
  thanhtienQuydoi: number | null
  thanhtoanQuydoi: number | null
  loaitien: string | null
  tygia: number | null
  refid: string | null
  paydataRequested: number | null
  paydataPaid: number | null
  contractNo: string | null
  journalMemo: string | null
  misaMapId: number | null
}

export interface MapBanHang {
  isMapped: string | null
  contractNo: string | null
  contractDate: string | null
  erpInv: string | null
  buyersRef: string | null
  cpCode: string | null
  shortName: string | null
  accountObjectCode: string | null
  tabName: string | null
  sochungtu: string | null
  misaContract: string | null
  contractExtract: string | null
  invExtract: string | null
  misaSohoadon: string | null
  dienGiai: string | null
  contractType: string | null
  refId: string | null
  misaMapId: number | null
}

export interface MapBanHangDiff {
  customsInvNo: string | null
  contractNo: string | null
  erpKhachhang: string | null
  misaLstCt: string | null
  misaKhachhang: string | null
}

export interface LookupItem {
  value: string
  label: string
  description?: string | null
}
export interface LookupResponse {
  items: LookupItem[]
  total: number
}

export interface InputValueWrite {
  inputType: string
  qualityCode: string | null
  values: Record<string, number | null>
}
export interface MapCpWrite {
  cpCode: string
  misaId: string | null
}
export interface MapServiceWrite {
  serviceCode: string
  misaId: string | null
}
export interface MapMuaHangWrite {
  misaMapId: number | null
  erpCp: string | null
  erpInvNo: string | null
  erpInvDate: string | null
  erpMisaCode: string | null
  misaCp: string | null
  misaInvNo: string | null
  misaInvDate: string | null
  misaRef: string | null
  misaObjectCode: string | null
}
export interface MapBanHangWrite {
  misaMapId: number | null
  erpInvNo: string | null
  erpContractNo: string | null
  erpCpCode: string | null
  misaCpCode: string | null
  misaRefId: string | null
  contractExtract: string | null
  invExtract: string | null
}

const TAG = 'ReportCashflow' as const

const reportCashflowApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getCashflowWeeks: b.query<WeekColumn[], void>({
      query: () => '/report-cashflow/weeks',
      providesTags: [TAG],
    }),
    getCashflowInput: b.query<CashflowListResponse<InputDetail>, { page: string }>({
      query: ({ page }) => ({ url: '/report-cashflow/input', params: { page } }),
      providesTags: [TAG],
    }),
    getCashflowBankBalance: b.query<CashflowListResponse<BankBalance>, void>({
      query: () => '/report-cashflow/bank-balance',
      providesTags: [TAG],
    }),
    getCashflowMapCp: b.query<CashflowListResponse<MapCp>, void>({
      query: () => '/report-cashflow/map-cp',
      providesTags: [TAG],
    }),
    getCashflowMapCpNeed: b.query<CashflowListResponse<MapCp>, void>({
      query: () => '/report-cashflow/map-cp/need',
      providesTags: [TAG],
    }),
    getCashflowMapCpDup: b.query<CashflowListResponse<MapCp>, void>({
      query: () => '/report-cashflow/map-cp/dup',
      providesTags: [TAG],
    }),
    getCashflowMapService: b.query<CashflowListResponse<MapService>, void>({
      query: () => '/report-cashflow/map-service',
      providesTags: [TAG],
    }),
    getCashflowMapServiceDup: b.query<CashflowListResponse<MapService>, void>({
      query: () => '/report-cashflow/map-service/dup',
      providesTags: [TAG],
    }),
    getCashflowMisaObject: b.query<CashflowListResponse<MisaObject>, void>({
      query: () => '/report-cashflow/misa-object',
      providesTags: [TAG],
    }),
    getCashflowMapMuaHang: b.query<CashflowListResponse<MapMuaHang>, void>({
      query: () => '/report-cashflow/map-muahang',
      providesTags: [TAG],
    }),
    getCashflowMapMuaHangNeed: b.query<CashflowListResponse<MapMuaHang>, void>({
      query: () => '/report-cashflow/map-muahang/need',
      providesTags: [TAG],
    }),
    getCashflowMisaMuaHang: b.query<{ items: Record<string, unknown>[] }, void>({
      query: () => '/report-cashflow/map-muahang/misa',
      providesTags: [TAG],
    }),
    getCashflowMapBanHang: b.query<CashflowListResponse<MapBanHang>, void>({
      query: () => '/report-cashflow/map-banhang',
      providesTags: [TAG],
    }),
    getCashflowMapBanHangDiff: b.query<CashflowListResponse<MapBanHangDiff>, void>({
      query: () => '/report-cashflow/map-banhang/diff',
      providesTags: [TAG],
    }),
    getCashflowMisaBanHang: b.query<{ items: Record<string, unknown>[] }, void>({
      query: () => '/report-cashflow/map-banhang/misa',
      providesTags: [TAG],
    }),
    // Lookups (lazy — dialogs)
    getCashflowLookup: b.query<LookupResponse, { kind: string; q?: string }>({
      query: ({ kind, q }) => ({ url: `/report-cashflow/lookups/${kind}`, params: q ? { q } : undefined }),
    }),
    getCashflowDetailLookup: b.query<{ items: Record<string, unknown>[] }, { kind: string; q?: string }>({
      query: ({ kind, q }) => ({ url: `/report-cashflow/detail-lookups/${kind}`, params: q ? { q } : undefined }),
    }),
    // Access: DLookUp price_from_g2 by quality code (Input Value dialog, live).
    getCashflowPriceFromG2: b.query<{ priceFromG2: number | null }, string>({
      query: (qualityCode) => ({ url: '/report-cashflow/price-from-g2', params: { qualityCode } }),
    }),
    // Mutations
    saveCashflowInput: b.mutation<{ message: string }, InputValueWrite>({
      query: (body) => ({ url: '/report-cashflow/input', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapCp: b.mutation<{ message: string }, MapCpWrite>({
      query: (body) => ({ url: '/report-cashflow/map-cp', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapService: b.mutation<{ message: string }, MapServiceWrite>({
      query: (body) => ({ url: '/report-cashflow/map-service', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapMuaHang: b.mutation<{ message: string }, MapMuaHangWrite>({
      query: (body) => ({ url: '/report-cashflow/map-muahang', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    deleteCashflowMapMuaHang: b.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/report-cashflow/map-muahang/${id}`, method: 'DELETE' }),
      invalidatesTags: [TAG],
    }),
    saveCashflowMapBanHang: b.mutation<{ message: string }, MapBanHangWrite>({
      query: (body) => ({ url: '/report-cashflow/map-banhang', method: 'POST', body }),
      invalidatesTags: [TAG],
    }),
    deleteCashflowMapBanHang: b.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/report-cashflow/map-banhang/${id}`, method: 'DELETE' }),
      invalidatesTags: [TAG],
    }),
  }),
})

export const {
  useGetCashflowWeeksQuery,
  useGetCashflowInputQuery,
  useGetCashflowBankBalanceQuery,
  useGetCashflowMapCpQuery,
  useGetCashflowMapCpNeedQuery,
  useGetCashflowMapCpDupQuery,
  useGetCashflowMapServiceQuery,
  useGetCashflowMapServiceDupQuery,
  useGetCashflowMisaObjectQuery,
  useGetCashflowMapMuaHangQuery,
  useGetCashflowMapMuaHangNeedQuery,
  useGetCashflowMisaMuaHangQuery,
  useGetCashflowMapBanHangQuery,
  useGetCashflowMapBanHangDiffQuery,
  useGetCashflowMisaBanHangQuery,
  useGetCashflowLookupQuery,
  useGetCashflowDetailLookupQuery,
  useGetCashflowPriceFromG2Query,
  useSaveCashflowInputMutation,
  useSaveCashflowMapCpMutation,
  useSaveCashflowMapServiceMutation,
  useSaveCashflowMapMuaHangMutation,
  useDeleteCashflowMapMuaHangMutation,
  useSaveCashflowMapBanHangMutation,
  useDeleteCashflowMapBanHangMutation,
} = reportCashflowApi

export default reportCashflowApi
